import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { ThemeProvider } from "./context/ThemeContext";
import { useState, useEffect, lazy, Suspense } from "react";
import Layout from "./components/Layout";
import { CRPLLoadingScreen } from "./components/Loader";
import { ErrorBoundary } from "./components/ErrorBoundary";
import { RequirePermission, Landing } from "./components/RequirePermission";

// Aggressive badge removal using MutationObserver
const removeBadges = () => {
  const selectors = [
    '#emergent-badge',
    '[href*="emergent.sh"]',
    '[href*="emergent"]',
    'a[style*="position: fixed"][style*="bottom"]',
    '.emergent-badge',
    'div[class*="emergent"]',
    'a[id*="emergent"]'
  ];
  
  selectors.forEach(selector => {
    document.querySelectorAll(selector).forEach(el => {
      el.remove();
    });
  });
};

// Run on mount and observe for dynamic additions
if (typeof window !== 'undefined') {
  // Initial cleanup
  removeBadges();
  
  // Observe for any dynamically added badges
  const observer = new MutationObserver((mutations) => {
    removeBadges();
  });
  
  // Start observing once DOM is ready
  if (document.body) {
    observer.observe(document.body, { childList: true, subtree: true });
  } else {
    document.addEventListener('DOMContentLoaded', () => {
      observer.observe(document.body, { childList: true, subtree: true });
    });
  }
}

// Lazy load pages for better performance (code splitting)
const Login = lazy(() => import("./pages/Login"));
const DailyBrief = lazy(() => import("./pages/DailyBrief"));
const AskSales = lazy(() => import("./pages/AskSales"));
const Settings = lazy(() => import("./pages/Settings"));
// Show only in preview (never on production deployment).
const WhatsAppTemplates = lazy(() => import("./pages/WhatsAppTemplates"));
const ValidationReport = lazy(() => import("./pages/admin/ValidationReport"));
const SpaceIntel = lazy(() => import("./pages/space-intel/SpaceIntel"));
const PulseFeed = lazy(() => import("./pages/PulseFeed"));
const WarRoom = lazy(() => import("./pages/WarRoom"));

// Analysis Pages
const DepartmentAnalysis = lazy(() => import("./pages/analysis/DepartmentAnalysis"));
const FestivalAnalysis = lazy(() => import("./pages/analysis/FestivalAnalysis"));
const PeakHoursAnalysis = lazy(() => import("./pages/analysis/PeakHoursAnalysis"));
const SalespersonAnalysis = lazy(() => import("./pages/analysis/SalespersonAnalysis"));
const VendorAnalysis = lazy(() => import("./pages/analysis/VendorAnalysis"));
const MarketIntel = lazy(() => import("./pages/analysis/MarketIntel"));
const StockIntelligence = lazy(() => import("./pages/analysis/StockIntelligence"));
const ProductDeepDive = lazy(() => import("./pages/analysis/ProductDeepDive"));
const ItemRunRate = lazy(() => import("./pages/analysis/ItemRunRate"));
const DeadStockRecovery = lazy(() => import("./pages/analysis/DeadStockRecovery"));
const VendorScorecard = lazy(() => import("./pages/analysis/VendorScorecard"));
const ProductIntelligence = lazy(() => import("./pages/analysis/ProductIntelligence"));
const Returns = lazy(() => import("./pages/analysis/Returns"));
const ProcurementIntel = lazy(() => import("./pages/procurement/ProcurementIntel"));
const Vendor360 = lazy(() => import("./pages/procurement/Vendor360"));
const ProcurementLogistics = lazy(() => import("./pages/procurement/Logistics"));
const FestivalBuying = lazy(() => import("./pages/procurement/FestivalBuying"));
const HRDashboard = lazy(() => import("./pages/hr/HRDashboard"));
const IdealEmployeeDNA = lazy(() => import("./pages/hr/IdealEmployeeDNA"));
const ReviewPage = lazy(() => import("./pages/hr/ReviewPage"));
// Simple Dashboards (restricted category users) — Sales/Stocks/Overall with admin preview.
const OverallDashboard = lazy(() => import("./pages/scoped/OverallDashboard"));
const SalesDashboard = lazy(() => import("./pages/scoped/SalesDashboard"));
const StocksDashboard = lazy(() => import("./pages/scoped/StocksDashboard"));

const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return <CRPLLoadingScreen message="Checking authentication..." />;
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  return children;
};

function AppRoutes() {
  return (
    <ErrorBoundary fallbackMessage="The app encountered an error. Click below to refresh.">
      <Suspense fallback={<CRPLLoadingScreen message="Loading..." />}>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route
            path="/"
            element={
              <ProtectedRoute>
                <Layout />
              </ProtectedRoute>
            }
          >
            <Route index element={<Landing />} />
            <Route path="brief" element={<RequirePermission permKey="brief"><DailyBrief /></RequirePermission>} />
            <Route path="ask" element={<RequirePermission permKey="synapse"><AskSales /></RequirePermission>} />
            <Route path="pulse" element={<RequirePermission permKey="pulse"><PulseFeed /></RequirePermission>} />
            <Route path="warroom" element={<RequirePermission permKey="warroom"><WarRoom /></RequirePermission>} />
            <Route path="settings" element={<RequirePermission permKey="settings"><Settings /></RequirePermission>} />
            {/* WhatsApp Template Builder — visible only on the preview environment (see IS_PREVIEW check inside). */}
            <Route path="settings/whatsapp-templates" element={<RequirePermission permKey="settings"><WhatsAppTemplates /></RequirePermission>} />
            <Route path="space-intel" element={<RequirePermission permKey="spaceIntel"><SpaceIntel /></RequirePermission>} />
            <Route path="validation" element={<RequirePermission permKey="settings"><ValidationReport /></RequirePermission>} />
            {/* Analysis Routes */}
            <Route path="analysis/departments" element={<RequirePermission permKey="salesAnalytics.departments"><ErrorBoundary fallbackMessage="Department Analysis failed to load."><DepartmentAnalysis /></ErrorBoundary></RequirePermission>} />
            <Route path="analysis/festivals" element={<RequirePermission permKey="salesAnalytics.festivals"><ErrorBoundary fallbackMessage="Festival Analysis failed to load."><FestivalAnalysis /></ErrorBoundary></RequirePermission>} />
            <Route path="analysis/peak-hours" element={<RequirePermission permKey="salesAnalytics.peakHours"><ErrorBoundary fallbackMessage="Peak Hours Analysis failed to load."><PeakHoursAnalysis /></ErrorBoundary></RequirePermission>} />
            <Route path="analysis/salesperson" element={<RequirePermission permKey="salesAnalytics.salesperson"><ErrorBoundary fallbackMessage="Salesperson Analysis failed to load."><SalespersonAnalysis /></ErrorBoundary></RequirePermission>} />
            <Route path="analysis/vendors" element={<RequirePermission permKey="salesAnalytics.vendors"><ErrorBoundary fallbackMessage="Vendor Analysis failed to load."><VendorAnalysis /></ErrorBoundary></RequirePermission>} />
            <Route path="analysis/market-intel" element={<RequirePermission permKey="market.marketIntel"><ErrorBoundary fallbackMessage="Market Intel failed to load."><MarketIntel /></ErrorBoundary></RequirePermission>} />
            <Route path="analysis/stock" element={<RequirePermission permKey="stockIntel.stock"><ErrorBoundary fallbackMessage="Stock Intelligence failed to load."><StockIntelligence /></ErrorBoundary></RequirePermission>} />
            <Route path="analysis/product-deep-dive" element={<RequirePermission permKey="stockIntel.productDeepDive"><ErrorBoundary fallbackMessage="Product Deep Dive failed to load."><ProductDeepDive /></ErrorBoundary></RequirePermission>} />
            <Route path="analysis/item-run-rate" element={<RequirePermission permKey="stockIntel.stock"><ErrorBoundary fallbackMessage="Item Run Rate failed to load."><ItemRunRate /></ErrorBoundary></RequirePermission>} />
            <Route path="analysis/dead-stock-recovery" element={<RequirePermission permKey="stockIntel.deadStockRecovery"><ErrorBoundary fallbackMessage="Dead Stock Recovery failed to load."><DeadStockRecovery /></ErrorBoundary></RequirePermission>} />
            <Route path="analysis/vendor-scorecard" element={<RequirePermission permKey="stockIntel.vendorScorecard"><ErrorBoundary fallbackMessage="Vendor Scorecard failed to load."><VendorScorecard /></ErrorBoundary></RequirePermission>} />
            <Route path="analysis/product-intel" element={<RequirePermission permKey="salesAnalytics.productIntel"><ErrorBoundary fallbackMessage="Product Intelligence failed to load."><ProductIntelligence /></ErrorBoundary></RequirePermission>} />
            <Route path="analysis/returns" element={<RequirePermission permKey="salesAnalytics.returns"><ErrorBoundary fallbackMessage="Returns Analysis failed to load."><Returns /></ErrorBoundary></RequirePermission>} />
            <Route path="procurement" element={<RequirePermission permKey="procurement.purchaseIntel"><ErrorBoundary fallbackMessage="Procurement Intel failed to load."><ProcurementIntel /></ErrorBoundary></RequirePermission>} />
            <Route path="procurement/vendor-360" element={<RequirePermission permKey="procurement.vendor360"><ErrorBoundary fallbackMessage="Vendor 360 failed to load."><Vendor360 /></ErrorBoundary></RequirePermission>} />
            <Route path="procurement/logistics" element={<RequirePermission permKey="procurement.logistics"><ErrorBoundary fallbackMessage="Logistics failed to load."><ProcurementLogistics /></ErrorBoundary></RequirePermission>} />
            <Route path="procurement/festival-buying" element={<RequirePermission permKey="procurement.festivalBuying"><ErrorBoundary fallbackMessage="Festival Buying failed to load."><FestivalBuying /></ErrorBoundary></RequirePermission>} />
            <Route path="hr" element={<RequirePermission permKey="peopleIntel.hrOverview"><ErrorBoundary fallbackMessage="HR Analytics failed to load."><HRDashboard /></ErrorBoundary></RequirePermission>} />
            <Route path="hr/overview" element={<RequirePermission permKey="peopleIntel.hrOverview"><ErrorBoundary fallbackMessage="HR Analytics failed to load."><HRDashboard /></ErrorBoundary></RequirePermission>} />
            <Route path="hr/attendance" element={<RequirePermission permKey="peopleIntel.attendance"><ErrorBoundary fallbackMessage="HR Analytics failed to load."><HRDashboard /></ErrorBoundary></RequirePermission>} />
            <Route path="hr/attrition" element={<RequirePermission permKey="peopleIntel.attrition"><ErrorBoundary fallbackMessage="HR Analytics failed to load."><HRDashboard /></ErrorBoundary></RequirePermission>} />
            <Route path="hr/performance" element={<RequirePermission permKey="peopleIntel.performance"><ErrorBoundary fallbackMessage="HR Analytics failed to load."><HRDashboard /></ErrorBoundary></RequirePermission>} />
            <Route path="hr/workforce" element={<RequirePermission permKey="peopleIntel.workforce"><ErrorBoundary fallbackMessage="HR Analytics failed to load."><HRDashboard /></ErrorBoundary></RequirePermission>} />
            <Route path="hr/weekly-off" element={<RequirePermission permKey="peopleIntel.attendance"><ErrorBoundary fallbackMessage="HR Analytics failed to load."><HRDashboard /></ErrorBoundary></RequirePermission>} />
            <Route path="hr/ideal-employee" element={<RequirePermission permKey="peopleIntel.employeeDna"><ErrorBoundary fallbackMessage="Ideal Employee DNA failed to load."><IdealEmployeeDNA /></ErrorBoundary></RequirePermission>} />
            <Route path="hr/demographics" element={<RequirePermission permKey="peopleIntel.demographics"><ErrorBoundary fallbackMessage="HR Analytics failed to load."><HRDashboard /></ErrorBoundary></RequirePermission>} />
            <Route path="hr/interns" element={<RequirePermission permKey="peopleIntel.interns"><ErrorBoundary fallbackMessage="Interns tab failed to load."><HRDashboard /></ErrorBoundary></RequirePermission>} />
            {/* Review Centre promoted to top-level (also kept under /hr/review for backward compat) */}
            <Route path="hr/review" element={<RequirePermission permKey="review"><ErrorBoundary fallbackMessage="Review Centre failed to load."><ReviewPage /></ErrorBoundary></RequirePermission>} />
            <Route path="review" element={<RequirePermission permKey="review"><ErrorBoundary fallbackMessage="Review Centre failed to load."><ReviewPage /></ErrorBoundary></RequirePermission>} />
            {/* Simple Dashboards (restricted user categories). Route guard is
                inside the components — they check the user's `dashboards` list
                so we don't have to teach RequirePermission a 2nd axis. */}
            <Route path="scoped/overall" element={<ErrorBoundary fallbackMessage="Overall dashboard failed to load."><OverallDashboard /></ErrorBoundary>} />
            <Route path="scoped/sales" element={<ErrorBoundary fallbackMessage="Sales dashboard failed to load."><SalesDashboard /></ErrorBoundary>} />
            <Route path="scoped/stocks" element={<ErrorBoundary fallbackMessage="Stocks dashboard failed to load."><StocksDashboard /></ErrorBoundary>} />
          </Route>
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Suspense>
    </ErrorBoundary>
  );
}

function App() {
  return (
    <AuthProvider>
      <ThemeProvider>
        <BrowserRouter>
          <AppRoutes />
          <Toaster 
            position="top-center" 
            richColors 
            toastOptions={{
              style: {
                fontFamily: 'Inter, sans-serif',
              },
            }}
          />
        </BrowserRouter>
      </ThemeProvider>
    </AuthProvider>
  );
}

export default App;
